import * as React from 'react';
import {
  Alert,
  Button,
  Container,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Textarea,
  TextContent,
} from '@amzn/awsui-components-react-v3';
import { Validation } from 'aws-sdk/clients/d3aservice';

export interface ValidateFormDataWithD3aProps {
  readonly: boolean;
  formData: object;
  loading: boolean;
  isValid: boolean;
  isUnknown: boolean;
  validationList: Validation[];
  validationErrorMessage: string;
  validate: any;
  overrideReason: string;
  setOverrideReason: any;
}

export const ValidateFormDataWithD3a = (props: ValidateFormDataWithD3aProps) => {
  const statusIndicator = () => {
    if (props.loading) {
      return <Spinner />;
    } else if (props.isUnknown) {
      return <StatusIndicator type={'stopped'}>Unknown</StatusIndicator>;
    } else if (!!props.validationErrorMessage) {
      return <StatusIndicator type={'error'}>{props.validationErrorMessage}</StatusIndicator>;
    } else if (props.isValid) {
      return <StatusIndicator type='success'>Valid</StatusIndicator>;
    } else {
      return (
        <Alert type={'warning'} header={'Validation errors'}>
          <SpaceBetween size={'m'} direction={'vertical'}>
            <TextContent>
              There are some failed validations which are listed below. It is highly recommended to fix these before
              saving your responses. If you would like to override the validation errors, provide a justification below.
            </TextContent>
            <FormField label={'Override reason'} description={'Provide a reason for overriding the validations.'}>
              <Textarea
                readOnly={props.readonly}
                value={props.overrideReason}
                onChange={(e) => props.setOverrideReason(e.detail.value)}
                placeholder={'Override reason'}
                rows={4}
              />
            </FormField>
          </SpaceBetween>
        </Alert>
      );
    }
  };

  const listOfErrors = () => {
    return props.validationList
      .map((validation) => {
        const validationResult = validation.validationResult;
        const validationResultCast = validationResult as object;
        const entries = Object.entries(validationResultCast);
        // non-D3A validations
        if (!validation.d3aValidationTypes || validation.d3aValidationTypes.length == 0) {
          return entries.map((entry) => {
            const [_, value] = entry;
            return (
              <li>
                <b>{validation.attribute}: </b>
                {value?.reason}
              </li>
            );
          });
        }
        // D3A validations
        else {
          return (
            <li>
              <b>{validation.attribute}: </b>
              <ul>
                {entries.map((entry) => {
                  const [key, value] = entry;
                  return (
                    <li>
                      <StatusIndicator type={value?.isValid ? 'success' : 'error'}>{value?.reason}</StatusIndicator>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        }
      })
      .flat();
  };

  return (
    <Container
      header={
        <Header variant={'h2'} description={'Select "Validate" to check the form inputs.'}>
          Validation result
        </Header>
      }
    >
      <SpaceBetween size={'m'} direction={'vertical'}>
        <Button variant={'normal'} disabled={props.loading} onClick={props.validate}>
          Validate
        </Button>
        {statusIndicator()}
        {props.validationList?.length > 0 && (
          <TextContent>
            <ul>{listOfErrors()}</ul>
          </TextContent>
        )}
      </SpaceBetween>
    </Container>
  );
};
